import * as React from "react";
import {
	Alert,
	Button,
	Card,
	CardHeader,
	Chip,
	Collapse,
	Divider,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/ssr/ArrowCounterClockwise";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/ssr/ClockCounterClockwise";

import { formatDateTime } from "@/lib/admin-formatters";
import type { PlatformPrompt, PromptPayload, PromptRunner } from "@/hooks/api/use-prompt-settings";
import { usePromptRevisions } from "@/hooks/api/use-prompt-settings";

/** What the editor holds while it is being typed in. Strings throughout; nulls are made on save. */
interface Draft {
	promptText: string;
	providerPluginKey: string;
	model: string;
}

function draftOf(prompt: PlatformPrompt): Draft {
	return {
		promptText: prompt.promptText,
		providerPluginKey: prompt.providerPluginKey ?? "",
		model: prompt.model ?? "",
	};
}

/**
 * The saved line: which version this is, and who put it there.
 *
 * A seeded row has no author, and saying "saved by nobody" would be a small lie about a prompt
 * somebody is about to trust. It says what it is instead.
 */
function savedLine(prompt: PlatformPrompt): string {
	const version = `Version ${prompt.version}`;

	if (!prompt.updatedByName) return `${version} · ${prompt.version === 1 ? "as shipped" : "system update"}`;

	return `${version} · ${prompt.updatedByName} · ${formatDateTime(prompt.updatedAt)}`;
}

interface PromptEditorProps {
	prompt: PlatformPrompt;
	runners: PromptRunner[];
	isSaving: boolean;
	onSave: (id: string, payload: PromptPayload) => Promise<PlatformPrompt>;
	/** Present only on an account override — a platform default has nothing to fall back to. */
	onRemoveOverride?: (prompt: PlatformPrompt) => void;
}

/**
 * One prompt, editable.
 *
 * The prompt is the screen. Everything around it is one line of contract above the box and one
 * line of provenance below it — what the model is handed, what it has to answer with, and which
 * version of this text is live. The pipeline it feeds is not described here; a super-admin came
 * to this page to change wording, not to read about generation.
 *
 * The draft retains the version it was loaded from. Remote edits never replace unsaved text;
 * a successful save or an explicit reload advances the editor to the new version.
 */
export function PromptEditor({
	prompt,
	runners,
	isSaving: mutationSaving,
	onSave,
	onRemoveOverride,
}: PromptEditorProps): React.JSX.Element {
	const [base, setBase] = React.useState(prompt);
	const [draft, setDraft] = React.useState<Draft>(() => draftOf(prompt));
	const [historyOpen, setHistoryOpen] = React.useState(false);
	const [saveInFlight, setSaveInFlight] = React.useState(false);
	const isSaving = mutationSaving || saveInFlight;

	const saved = React.useMemo(() => draftOf(base), [base]);
	const dirty =
		draft.promptText !== saved.promptText ||
		draft.providerPluginKey !== saved.providerPluginKey ||
		draft.model !== saved.model;
	const newerVersionAvailable = prompt.version > base.version;

	// Background refreshes must not replace unsaved text or advance its expected version.
	React.useEffect(() => {
		if (!dirty && newerVersionAvailable) {
			setBase(prompt);
			setDraft(draftOf(prompt));
		}
	}, [prompt, dirty, newerVersionAvailable]);

	const loadLatest = (): void => {
		setBase(prompt);
		setDraft(draftOf(prompt));
	};

	// Fetched only once history is open: it carries every version's full text, which is the one
	// response on this screen that grows without limit.
	const { data: revisions, isLoading: revisionsLoading } = usePromptRevisions(prompt.id, historyOpen);

	// Every version but the one already in the box. "Version 4" is what the editor is showing.
	const earlier = (revisions ?? []).filter((revision) => revision.version !== prompt.version);

	const handleSave = async (): Promise<void> => {
		if (isSaving) return;
		setSaveInFlight(true);
		try {
			const updated = await onSave(prompt.id, {
				version: base.version,
				promptText: draft.promptText,
				providerPluginKey: draft.providerPluginKey || null,
				model: draft.model || null,
			});
			setBase(updated);
			setDraft(draftOf(updated));
		} catch {
			// The mutation reports the error; keep the draft and its version until the admin reloads.
		} finally {
			setSaveInFlight(false);
		}
	};

	return (
		<Card>
			<CardHeader
				title={prompt.title}
				action={
					prompt.accountId ? (
						// Only an override says whose it is. The platform rows are what the page line
						// already said they were, and a chip repeating it on every card is noise.
						<Chip label={prompt.accountName ?? "One account"} size="small" color="warning" variant="outlined" />
					) : null
				}
			/>
			<Divider />

			<Stack spacing={2.5} sx={{ p: 2 }}>
				{newerVersionAvailable && dirty ? (
					<Alert
						severity="warning"
						action={
							<Button color="inherit" disabled={isSaving} onClick={loadLatest}>
								Load latest
							</Button>
						}
					>
						Another administrator saved version {prompt.version}. Your draft is preserved. Copy any changes you want to
						keep before loading the latest version, which replaces this draft.
					</Alert>
				) : null}
				<Stack spacing={0.5}>
					<Typography variant="caption" color="text.secondary">
						Receives — {prompt.receives}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Returns — {prompt.returns}
					</Typography>
				</Stack>

				<TextField
					fullWidth
					multiline
					minRows={14}
					maxRows={40}
					disabled={isSaving}
					value={draft.promptText}
					onChange={(event) => setDraft((current) => ({ ...current, promptText: event.target.value }))}
					slotProps={{
						// The editor's own label: the captions above it are not associated with the box,
						// so without this a screen reader announces it as "edit text, blank".
						htmlInput: { "aria-label": `${prompt.title} prompt` },
						input: {
							sx: {
								fontFamily: "var(--mui-font-monospace, monospace)",
								fontSize: 13,
								lineHeight: 1.65,
								// The box is the point of the page, so it gets the contrast rather than
								// sitting at the same level as the card behind it.
								bgcolor: "var(--mui-palette-background-level1)",
							},
						},
					}}
				/>

				<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
					{/* With no service set up there is nothing to pick between, and a dropdown whose only
					    entry is "whatever the account uses" is not a choice. */}
					{runners.length > 0 || draft.providerPluginKey ? (
						<TextField
							select
							label="Service"
							size="small"
							disabled={isSaving}
							value={draft.providerPluginKey}
							onChange={(event) => setDraft((current) => ({ ...current, providerPluginKey: event.target.value }))}
							sx={{ minWidth: 220 }}
						>
							{/* The default, and the honest one: a platform setting must not assume every
							    client has bought a particular vendor. */}
							<MenuItem value="">Whatever the account uses</MenuItem>
							{runners.map((runner) => (
								<MenuItem key={runner.key} value={runner.key}>
									{runner.title}
								</MenuItem>
							))}
						</TextField>
					) : null}

					<TextField
						label="Model"
						size="small"
						placeholder="As the service is set"
						disabled={isSaving}
						value={draft.model}
						onChange={(event) => setDraft((current) => ({ ...current, model: event.target.value }))}
						sx={{ minWidth: 220 }}
					/>
				</Stack>
			</Stack>

			<Divider />

			<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" }, p: 2 }}>
				<Typography variant="caption" color="text.secondary" sx={{ flex: "1 1 auto" }}>
					{savedLine(base)}
				</Typography>

				<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
					{/* Version 1 has no history to open — there is nothing this text replaced. */}
					{prompt.version > 1 ? (
						<Button
							size="small"
							color="inherit"
							startIcon={<ClockCounterClockwiseIcon />}
							onClick={() => setHistoryOpen((open) => !open)}
						>
							History
						</Button>
					) : null}

					{onRemoveOverride ? (
						<Button size="small" color="inherit" onClick={() => onRemoveOverride(prompt)}>
							Use the Platform Default
						</Button>
					) : null}

					{dirty ? (
						<Button size="small" color="inherit" disabled={isSaving} onClick={() => setDraft(saved)}>
							Discard
						</Button>
					) : null}

					<Button
						size="small"
						variant="contained"
						disabled={!dirty || isSaving || newerVersionAvailable}
						onClick={handleSave}
					>
						Save
					</Button>
				</Stack>
			</Stack>

			<Collapse in={historyOpen} unmountOnExit>
				<Divider />
				<Stack divider={<Divider />}>
					{revisionsLoading ? (
						<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
							Loading…
						</Typography>
					) : null}

					{!revisionsLoading && earlier.length === 0 ? (
						<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
							Nothing has replaced this version yet.
						</Typography>
					) : null}

					{earlier.map((revision) => (
						<Stack
							key={revision.id}
							direction={{ xs: "column", sm: "row" }}
							spacing={1.5}
							sx={{ alignItems: { sm: "center" }, px: 2, py: 1.25 }}
						>
							<Typography variant="body2" sx={{ flex: "1 1 auto", minWidth: 0 }}>
								Version {revision.version}
								<Typography component="span" variant="body2" color="text.secondary">
									{revision.changedByName ? ` · ${revision.changedByName}` : " · as shipped"} ·{" "}
									{formatDateTime(revision.createdAt)}
								</Typography>
							</Typography>

							{/* Loads it into the box rather than saving it. Restoring old wording is still an
							    edit somebody signs for, and it lands as a new version like any other. */}
							<Button
								size="small"
								color="inherit"
								disabled={isSaving}
								startIcon={<ArrowCounterClockwiseIcon />}
								onClick={() =>
									setDraft({
										promptText: revision.promptText,
										providerPluginKey: revision.providerPluginKey ?? "",
										model: revision.model ?? "",
									})
								}
							>
								Bring Back
							</Button>
						</Stack>
					))}
				</Stack>
			</Collapse>
		</Card>
	);
}
